// src/oura/screens/ClientReviews.tsx
//
// Staff "Client": what is out with the client, and what we owe them.
//
//   1. Documents: 1 row per Google Doc in the client's folder, read by Oura,
//      grouped by who moves next, each opening in a drawer (the board). New
//      questions for the client go through these docs (YW, 29 Sep 2026), so
//      Oura's own pack tools (New pack, Choose what to ask, the pack
//      composer) are gone from here. Pack data is untouched.
//   2. Requests: everything the client started (a change from their Work
//      page, a call), then the questions already sent in a pack, grouped by
//      who moves next, each opening in a drawer with its thread. The
//      questions card hides when there are none at all.
//   3. This week's note to the merchant, then the client's Drive folder.
//
// Sits inside the business chrome and reads the same tracker the Fixes screen
// shows, so the two never disagree about which fixes exist.
//
// Nine links land here. The six ?params are read once and then taken off the URL (with
// replace, so Back does not replay it):
//   ?item=<ask id>      that question, open in the drawer (Overview's rows).
//   ?request=<id>       that request, open in the drawer over the Requests
//                       card (the "asked for a change" notice). A call
//                       request's id opens that call.
//   ?ask=<task id>      the Documents card, unfolded and in view. Old links
//                       from a fix's "Ask the client"; the task id is ignored.
//   ?compose=note       this week's note, in view with the cursor in it.
//   ?doc=<doc id>       that doc, open in its drawer over the Documents card
//                       (the "answered in" notices, All clients' rows).
//   ?folder=1           the Client folder card, unfolded, with the cursor in
//                       its link field when no folder is linked (Connectors'
//                       "Link the client's folder", All clients' "Link the folder").
//   #documents          the Documents card, unfolded and in view (a fix's
//                       "Open the documents", Overview's client rows with no
//                       question). A hash, so it stays on the URL.
//   #requests           the Requests card, unfolded and in view (Overview's
//                       "N requests need us"). A hash, so it stays on the URL.
//   #questions          Questions to the client, unfolded and in view on the
//                       questions waiting (Fixes' "N questions on Client"),
//                       or the Documents card when no question was ever sent.
//                       A hash, so it stays on the URL.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { EyeIcon, MoreHorizontalIcon, RocketIcon, UploadIcon, UserPlusIcon } from "lucide-react";
import type { Business } from "../contract";
import type { BusinessTracker } from "../tracker";
import { useVisiblePoll } from "../lib/useVisiblePoll";
import { cn } from "../lib/cn";
import { SwitchWorkspaceDialog } from "@container/dialog/dialog-switch-workspace";
import { NotificationBell } from "../components/NotificationBell";
import { ouraHref, showOnboarding } from "../nav";
import { useOuraRole } from "../role";
import { OnboardingStrip } from "../components/onboarding/OnboardingStrip";
import { googleProfileFolds } from "../onboarding/recordCheck";
import { StartPlanWizard, scopeVerdictOf } from "../components/StartPlanWizard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@component/dropdown-menu";
import { isOuraLiveApiEnabled, listBusinesses, viewAsMerchant } from "@service/aeo";
import type { AeoScope } from "@service/aeo/types";
import { useSnackbar } from "@hook/use-snackbar";
import { Bento, BentoStack, FOCUS_RING, FoldScope, PillButton, TYPE, useFoldOpener } from "@oura/ui";
import { ScreenSentence } from "../components/client/ScreenSentence";
import { groupAsks } from "../components/client/asks";
import { QuestionsCard } from "../components/client/AsksCard";
import { RequestsCard } from "../components/client/RequestsCard";
import { AskDrawer } from "../components/client/AskDrawer";
import { askSeqFor, type AskSeq, type AskTarget } from "../components/client/askTargets";
import { groupRequests, NO_MARKS, unreadMarks, useOnChangeRequestsChanged, type UnreadMarks } from "../components/client/changeRequests";
import { clientSentence, listText } from "../components/client/pageSentence";
import { NoteComposer } from "../components/client/NoteComposer";
import { CreateLoginDialog, UploadReportDialog } from "../components/client/ClientDialogs";
import { AccessLoginChip } from "../components/client/AccessLoginChip";
import { DocumentsBoard } from "../components/docs/DocumentsBoard";
import type { CreateRequest } from "../components/docs/createRequest";
import { ReportsCard } from "../components/client/ReportsCard";
import { GbpProfileCard } from "../components/client/GbpProfileCard";
import { FOLDER_LINK_FIELD_ID, FolderCard } from "../components/docs/FolderCard";
import { groupOfDoc } from "../components/docs/boardModel";
import { useBusinessDocuments, useChangeRequests } from "@oura/docs";
import { ASK_OURA_ON } from "../ask/flag";
import { DOCS_WRITE_ON } from "../docs/writeFlag";
import { useWritesPaused } from "@context/maintenance";
import {
    listCallRequests,
    listNotifications,
    listPacks,
    listThreads,
    noticeLink,
    type CallRequest,
    type Pack,
    type Thread,
} from "@service/aeo/clientReview";

const SCOPE = "staff-client";

const MENU = "min-w-[220px] rounded-xl border-0 bg-white p-1 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)] ring-1 ring-black/[0.06]";
const MENU_ITEM = cn(TYPE.tc, "cursor-pointer rounded-lg px-2.5 py-2 text-gray-900 focus:bg-gray-100");

/**
 * What the bell has not marked read, sorted into questions, change requests
 * and calls: the "New" tag on a row. A failed read is no tags, never a
 * broken screen.
 */
async function readUnreadMarks(businessId: string): Promise<UnreadMarks> {
    try {
        return unreadMarks(await listNotifications("staff", businessId));
    } catch {
        return NO_MARKS;
    }
}

export function ClientReviews({ business, tracker, businessId }: { business: Business; tracker: BusinessTracker; businessId: string }) {
    // Opens a card in place from a link: any card may have been folded by hand.
    const openCard = useFoldOpener(SCOPE);
    const writesPaused = useWritesPaused();
    const snackbar = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const taskById = useMemo(() => new Map(tracker.weeks.flatMap((w) => w.tasks).map((t) => [t.id, t] as const)), [tracker]);
    const [packs, setPacks] = useState<Pack[] | null>(null);
    const [threads, setThreads] = useState<Thread[]>([]);
    const [calls, setCalls] = useState<CallRequest[]>([]);
    const [marks, setMarks] = useState<UnreadMarks>(NO_MARKS);
    /** Bumped after an upload, so the Reports card reads again instead of waiting for the tab to come back. */
    const [reportsSeq, setReportsSeq] = useState(0);
    /** The ask open in the drawer, with the group its row sat in (null for a link: the drawer uses its current group). */
    const [ask, setAsk] = useState<{ target: AskTarget; seq: AskSeq | null } | null>(null);

    // The client's change requests: their own read, with its own failure and
    // its own "not served yet", so a failed read of them never blanks the
    // packs. One read for the Requests card, the page sentence and the
    // drawer, so they never disagree. The drawer's writes read it again at once.
    const requests = useChangeRequests(businessId);
    useOnChangeRequestsChanged(requests.reload);

    // The documents Oura reads from the client's folder. One read for the
    // board, the folder card and the page sentence, so they never disagree.
    const docs = useBusinessDocuments(businessId);
    // One drawer at a time: opening a doc closes an ask, and the other way round.
    const [openDocId, setOpenDocId] = useState<string | null>(null);
    const openAsk = useCallback((t: AskTarget | null, seq: AskSeq | null = null) => {
        if (t) setOpenDocId(null);
        setAsk(t ? { target: t, seq } : null);
    }, []);
    const openDoc = useCallback((id: string | null) => {
        if (id) setAsk(null);
        setOpenDocId(id);
    }, []);

    // On the mock nothing rejects, so these loads never needed a catch. Against
    // the real API every one of them can, and an uncaught rejection leaves
    // `packs` null for good: the screen sits on its loading state with nothing
    // to explain it. A refresh that fails keeps the rows already on screen.
    const [loadError, setLoadError] = useState<string | null>(null);

    const refresh = async () => {
        try {
            const [p, t, c] = await Promise.all([listPacks(businessId), listThreads(businessId), listCallRequests(businessId)]);
            setPacks(p); setThreads(t); setCalls(c); setLoadError(null);
        } catch {
            /* keep what is on screen; the next poll tries again */
        }
        setMarks(await readUnreadMarks(businessId));
    };
    useEffect(() => {
        let cancelled = false;
        void Promise.all([listPacks(businessId), listThreads(businessId), listCallRequests(businessId)]).then(([p, t, c]) => {
            if (cancelled) return;
            setPacks(p); setThreads(t); setCalls(c); setLoadError(null);
        }).catch(() => {
            if (cancelled) return;
            // An empty list is a real answer; a failed load is not. Say which.
            setPacks([]);
            setLoadError("Could not load the questions and calls for this business. It will retry in a few seconds.");
        });
        void readUnreadMarks(businessId).then((m) => { if (!cancelled) setMarks(m); });
        return () => { cancelled = true; };
    }, [businessId]);

    // The 5-second poll came from the in-process mock, where the other side's
    // reply had to appear while you watched. Against the real API it is a
    // request every 5 seconds per open tab (#3676); refreshing when the tab
    // comes back to the front covers the same case honestly.
    useVisiblePoll(() => { void refresh(); }, 60_000, [businessId]);

    const [loginOpen, setLoginOpen] = useState(false);
    const [startPlanOpen, setStartPlanOpen] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);

    // "View as the merchant" opens the merchant's real screens, read-only, in a
    // NEW TAB: confirm, ask the server for a view-as session, open it. The staff
    // session in this tab is untouched, which is the whole difference from the
    // dev-cookie preview it replaces: that one flipped a 30-day cookie, so every
    // tab became the merchant and the way back was a menu item you had to know
    // about. The server creates the workspace on first use (#4159 /
    // ourteam-backend#5823), so the first press can take a moment: the button spins.
    const [switchOpen, setSwitchOpen] = useState(false);
    const [switching, setSwitching] = useState(false);
    // Staff only. `useOuraRole` resolves admin and super_admin to oura-staff; the
    // dev-cookie merchant preview is not staff. No fixture answer exists for a
    // session token, so it is also hidden while the live API is off.
    const canViewAsMerchant = useOuraRole() === "oura-staff" && isOuraLiveApiEnabled;

    const enterMerchantWorkspace = async () => {
        // The first use creates the merchant's workspace, so maintenance pauses this too.
        if (writesPaused) { setSwitchOpen(false); return; }
        // The tab is reserved here, inside the click: the first call may create
        // the workspace and take a while, and a window opened after that wait
        // loses the click's user activation and is blocked as a pop-up.
        const merchantTab = window.open("about:blank", "_blank");
        if (!merchantTab) {
            snackbar.showError("Allow pop-ups to open the merchant's screens.");
            setSwitchOpen(false);
            return;
        }
        merchantTab.opener = null;
        setSwitching(true);
        try {
            const result = await viewAsMerchant(businessId);
            if (result.ok) {
                const url = new URL(`https://${result.session.tokenDomain}/switch-workspace`);
                url.searchParams.set("token", result.session.accessToken);
                // `placeholder=1` only lets the merchant tab's banner add "This
                // merchant has no login yet" (#4112); the token decides the rest.
                if (result.session.placeholder === true) url.searchParams.set("placeholder", "1");
                merchantTab.location.replace(url.toString());
            } else {
                // The server's own sentence (403 not staff, 404 unknown business).
                // Nothing was opened or stored, so the button can be pressed again.
                merchantTab.close();
                snackbar.showError(result.message);
            }
        } catch {
            merchantTab.close();
            snackbar.showError("Could not open the merchant's screens. Please try again.");
        } finally {
            setSwitching(false);
            setSwitchOpen(false);
        }
    };
    /**
     * The scope verdict for step two (#3712 AC2 / ourteam-backend#4910).
     *
     * Read off the LIST row, because that is the only payload carrying it:
     * `GET /businesses/{id}` builds its own dict and `scope_blocks` is joined in
     * `business_list_items` alone. Fetched when the wizard opens rather than on
     * every render of this tab: a staffer opens Start a plan deliberately, and
     * one request there is cheaper than a field this screen never otherwise
     * needs. Null on failure, which reads as "not scored", the same honest
     * state as a business nobody has scanned.
     */
    const [scope, setScope] = useState<AeoScope | null>(null);
    useEffect(() => {
        if (!startPlanOpen) return;
        let cancelled = false;
        void listBusinesses()
            .then((all) => {
                if (cancelled) return;
                setScope(all.find((b) => b.id === businessId)?.scope ?? null);
            })
            .catch(() => { if (!cancelled) setScope(null); });
        return () => { cancelled = true; };
    }, [startPlanOpen, businessId]);

    const groups = useMemo(() => groupAsks(packs ?? [], threads, marks.items), [packs, threads, marks]);
    /**
     * Questions to the client shows only once the packs are read and a
     * question was sent, or when the read failed (the card says so). No new
     * questions start here any more, so an empty card would only ever say
     * "none". Hidden while loading too: a card that appears is a smaller jump
     * than one that disappears.
     */
    const showQuestions = loadError !== null || (packs !== null && groups.needsUs.length + groups.waiting.length + groups.answered.length > 0);
    /** The packs are read, or the read failed (packs is [] then): the folder card's column is known. */
    const questionsRead = packs !== null;
    const requestGroups = useMemo(() => groupRequests(requests.data ?? [], calls), [requests.data, calls]);
    /** Whether G5 (the monthly small change) is in the contract, from the board's code rows; null when they could not be read. */
    const hasG5 = docs.data ? docs.data.codes.some((r) => r.code === "G5" && r.inContract) : null;
    /** A call's question by title, from the packs on screen. */
    const itemTitle = useCallback((itemId: string) => {
        for (const p of packs ?? []) for (const it of p.items) if (it.id === itemId) return it.title;
        return null;
    }, [packs]);

    // The links that land here (see the head of this file).
    const [noteFocus, setNoteFocus] = useState(0);
    const dropParams = useCallback(
        (...names: string[]) =>
            setSearchParams((params) => {
                names.forEach((n) => params.delete(n));
                return params;
            }, { replace: true }),
        [setSearchParams],
    );

    // ?ask= and ?compose= need only the page, so they land at once. ?ask= is
    // an old link into "Choose what to ask", which is gone: questions for the
    // client now go through the docs, so it opens the Documents card, whatever
    // fix it named.
    const askParam = searchParams.get("ask");
    const composeParam = searchParams.get("compose");
    useEffect(() => {
        if (!askParam && !composeParam) return;
        if (askParam) openCard("documents");
        if (composeParam === "note") {
            openCard("note");
            setNoteFocus((n) => n + 1);
        }
        dropParams("ask", "compose");
    }, [askParam, composeParam, openCard, dropParams]);

    // ?item= waits for the packs: it is known to be gone only once they have
    // loaded. A failed load keeps the link for the retry, rather than telling
    // someone an ask closed that nobody could read.
    const itemParam = searchParams.get("item");
    useEffect(() => {
        if (!itemParam || packs === null || loadError) return;
        if (packs.some((p) => p.items.some((i) => i.id === itemParam))) {
            // Its card and its own group open first (either may have been
            // folded by hand), so focus has a row to come back to.
            openCard("asks");
            const inGroup = (list: typeof groups.needsUs) => list.some((a) => a.item.id === itemParam);
            const groupId = inGroup(groups.needsUs) ? "asks.needs_us" : inGroup(groups.waiting) ? "asks.waiting" : "asks.answered";
            openCard(groupId);
            // The drawer walks the question's current group.
            openAsk({ kind: "item", itemId: itemParam }, askSeqFor(groupId, groups, requestGroups));
        } else snackbar.showSuccess("That question is no longer open.", "Every question still out is on Questions to the client.");
        dropParams("item");
    }, [itemParam, packs, groups, requestGroups, loadError, snackbar, dropParams, openAsk, openCard]);

    // ?request= waits for the requests and the calls: it is known to be gone
    // only once both are read. A failed read keeps the link for the retry.
    // "Not served yet" is an answer: a change cannot be found then, a call can.
    const requestParam = searchParams.get("request");
    const requestsNotServed = requests.error?.reason === "notServed";
    useEffect(() => {
        if (!requestParam || packs === null || loadError) return;
        if (requests.status === "loading" || (requests.status === "error" && !requestsNotServed)) return;
        const change = requests.data?.find((c) => c.id === requestParam);
        const call = change ? undefined : calls.find((c) => c.id === requestParam);
        if (change || call) {
            // The card and the request's own group, either of which may be folded.
            openCard("requests");
            const inGroup = (list: typeof requestGroups.done) => list.some((r) => r.id === requestParam);
            const groupId = inGroup(requestGroups.needsUs) ? "requests.needs_us" : inGroup(requestGroups.scheduled) ? "requests.scheduled" : "requests.done";
            openCard(groupId);
            // The drawer walks the request's current group.
            openAsk(change ? { kind: "change", changeId: change.id, change } : { kind: "call", callId: requestParam }, askSeqFor(groupId, groups, requestGroups));
        } else if (requestsNotServed) {
            snackbar.showSuccess("Oura can't read the client's requests here yet.");
        } else {
            snackbar.showSuccess("That request is no longer here.", "Every request from the client is on the Requests card.");
        }
        dropParams("request");
    }, [requestParam, packs, loadError, requests.status, requests.data, requestsNotServed, calls, groups, requestGroups, openCard, openAsk, snackbar, dropParams]);

    // ?doc= waits for the board. A failed read keeps the link for the retry;
    // a doc that is no longer in the folder says so.
    const docParam = searchParams.get("doc");
    const docsNotServed = docs.status === "error" && docs.error?.reason === "notServed";
    useEffect(() => {
        if (!docParam) return;
        // "Not served yet" is an answer, not a failure to retry: say so once
        // and drop the link, rather than leave it in the address in silence.
        if (docsNotServed) {
            snackbar.showSuccess("Oura can't read this client's documents here yet.");
            dropParams("doc");
            return;
        }
        if (docs.status !== "ready" || !docs.data) return;
        const doc = docs.data.documents.find((d) => d.id === docParam);
        if (doc) {
            // The card, then the doc's group, which may start folded.
            openCard("documents");
            openCard(`documents.${groupOfDoc(doc)}`);
            openDoc(docParam);
        } else {
            snackbar.showSuccess("That document is no longer in the folder.", "Every doc Oura reads is on the Documents card.");
        }
        dropParams("doc");
    }, [docParam, docsNotServed, docs.status, docs.data, openCard, openDoc, snackbar, dropParams]);

    // ?create=<doc type> (Ask Oura, behind VITE_ENABLE_OURA_ASK) waits for the board: Create a doc
    // opens on the Documents card with that doc type picked ("new" picks none).
    // Nothing is made until a person presses Create the doc in the sheet.
    const createParam = ASK_OURA_ON ? searchParams.get("create") : null;
    // The request carries the client it was read on, so the board drops it on any other client.
    const [createRequest, setCreateRequest] = useState<CreateRequest | null>(null);
    useEffect(() => {
        if (!createParam) return;
        // Writes are off here, so the sheet cannot open. Say why and drop the link, rather than clear it in silence.
        if (!DOCS_WRITE_ON) {
            snackbar.showError("Creating a doc is not switched on here.");
            dropParams("create", "codes", "outlet");
            return;
        }
        if (docsNotServed) {
            snackbar.showSuccess("Oura can't read this client's documents here yet.");
            dropParams("create");
            return;
        }
        if (docs.status !== "ready" || !docs.data) return;
        openCard("documents");
        setOpenDocId(null);
        setAsk(null);
        // The codes Delivery would prefill for the same step (`&codes=G1`), so the sheet never ticks codes another doc clears.
        const codes = (searchParams.get("codes") ?? "").split(",").map((c) => c.trim()).filter(Boolean);
        setCreateRequest({ businessId, docType: createParam === "new" ? null : createParam, codes, outlet: searchParams.get("outlet") });
        dropParams("create", "codes", "outlet");
    }, [createParam, searchParams, docsNotServed, docs.status, docs.data, openCard, snackbar, dropParams, businessId]);

    // ?folder=1 waits for the board, so it knows whether a folder is linked.
    // A failed read still unfolds the card, which shows the failure. It waits
    // for the packs too: they decide which column the folder card sits in,
    // and a card that moves column mounts again and loses the cursor.
    const folderParam = searchParams.get("folder");
    useEffect(() => {
        if (!folderParam || docs.status === "loading" || packs === null) return;
        openCard("folder");
        if (docs.status === "ready" && docs.data && !docs.data.folder) {
            // After the card has unfolded, the cursor goes in the link field.
            window.setTimeout(() => document.getElementById(FOLDER_LINK_FIELD_ID)?.focus(), 350);
        }
        dropParams("folder");
    }, [folderParam, docs.status, docs.data, packs, openCard, dropParams]);

    // The page sentence waits for every read, so it never says "nothing"
    // before the data lands. A read that failed leads, once for the page
    // ("Could not read the documents, the questions or the client's
    // requests."), since every count after it may be short. The questions
    // and the calls come in one read: when it fails, both are unknown. Then
    // what needs us, what is late and what has a red flag, each count said
    // once (pageSentence.tsx). "Not served yet" is not a failure, since
    // those cards say so themselves, but it is not a read either: the
    // sentence then never says nothing needs us.
    const unread = [
        docs.status === "error" && docs.error?.reason !== "notServed" && "the documents",
        loadError !== null && "the questions",
        ((requests.error !== null && !requestsNotServed) || loadError !== null) && "the client's requests",
    ].filter((w): w is string => !!w);
    const failedPart = unread.length ? `Could not read ${listText(unread, "or")}.` : null;
    const othersRead = loadError === null && requests.status === "ready" && requests.error === null;
    const sentence = clientSentence({ docs, asks: groups, requests: requestGroups, failedPart, othersRead });
    const sentenceReady = packs !== null && docs.status !== "loading" && requests.status !== "loading";

    // #documents: the Documents card, unfolded and in view at once, since
    // it is the first card and nothing above it can push it down.
    // #requests: the Requests card, unfolded and brought into view once the
    // cards above it have their rows (scrolled to earlier, it would be pushed
    // off screen as they land). #questions: the same for Questions to the
    // client, on its waiting group; with no question sent there is no such
    // card, so it opens Documents, where the client is asked now. Once per hash.
    const { hash } = useLocation();
    const revealedHash = useRef<string | null>(null);
    useEffect(() => {
        if (revealedHash.current === hash) return;
        if (hash === "#documents") {
            revealedHash.current = hash;
            openCard("documents");
        } else if (hash === "#requests" && sentenceReady) {
            revealedHash.current = hash;
            // "N requests need us" lands on them, even in a group folded by hand.
            openCard("requests");
            openCard("requests.needs_us");
        } else if (hash === "#questions" && sentenceReady) {
            revealedHash.current = hash;
            if (showQuestions) {
                // "N questions on Client" counts the ones waiting on the client.
                openCard("asks");
                openCard("asks.waiting");
            } else openCard("documents");
        } else if (googleProfileFolds(hash).length > 0) {
            // The Business record card's "Not recorded" (#4153): the Google
            // profile card, and the outlet's own block when the link names one.
            revealedHash.current = hash;
            googleProfileFolds(hash).forEach((id) => openCard(id));
        }
    }, [hash, sentenceReady, showQuestions, openCard]);

    return (
        <FoldScope value={SCOPE}>
            {sentenceReady && sentence && <ScreenSentence>{sentence}</ScreenSentence>}
            <div className="space-y-5 pb-24">
                {loadError && (
                    <div className={cn(TYPE.tb, "rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200")}>
                        {loadError}
                    </div>
                )}
                {showOnboarding && (
                    <OnboardingStrip
                        businessId={businessId}
                        business={{ name: business.name, domain: business.domain }}
                        onboardingHref={ouraHref("onboarding", { role: "oura-staff", businessId })}
                    />
                )}

                {/* The toolbar: the one merchant-access control, More and
                    the bell. New pack left it on 29 Sep 2026 (YW): the
                    client's questions go through the docs below. */}
                <div className="flex flex-wrap items-center gap-2">
                    {/* View as the merchant is offered for every business
                        (#4159): the server creates the workspace on first use,
                        so it no longer waits for the access login, and
                        `companyId` says nothing about whether a login was
                        sent. It sits first on the toolbar, not in More, so it
                        stays in sight as staging made it (d8b5a74e), and spins
                        while the session opens (57151bc9). Without onboarding
                        the login is created from this toolbar, so Create
                        merchant login stays beside it; with onboarding the
                        first login is sent from the Onboarding tab, and a
                        later one is "Add a login" there. */}
                    {canViewAsMerchant && (
                        <>
                            <PillButton icon={EyeIcon} busy={switching} writes onClick={() => setSwitchOpen(true)}>
                                View as the merchant
                            </PillButton>
                            {/* Seeing their screens is not the merchant being able to sign in (#4160). */}
                            <AccessLoginChip businessId={businessId} />
                        </>
                    )}
                    {!showOnboarding && (
                        <PillButton icon={UserPlusIcon} writes onClick={() => setLoginOpen(true)}>
                            Create merchant login
                        </PillButton>
                    )}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <PillButton icon={MoreHorizontalIcon}>More</PillButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className={MENU}>
                            {/* Supersedes "Create merchant login" for a NEW
                                activation: that one mints a login on its own,
                                which is how a business ended up with a
                                workspace and no plan. This does the plan, the
                                workspace and the login in a single server-side
                                transaction (#3712 / ourteam-backend#4809). The
                                login alone stays for a business whose plan is
                                already live. */}
                            {/* Onboarding reads the same signed quote and takes over where it is on. */}
                            {!showOnboarding && (
                                <DropdownMenuItem className={MENU_ITEM} disabled={writesPaused} onSelect={() => setStartPlanOpen(true)}>
                                    <RocketIcon aria-hidden className="text-gray-500" />
                                    Start a plan
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem className={MENU_ITEM} disabled={writesPaused} onSelect={() => setReportOpen(true)}>
                                <UploadIcon aria-hidden className="text-gray-500" />
                                Upload a report
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {/* The tab is five cards tall; these go straight to one, unfolding it
                        if it was folded (Jack, 22 Oct 2026: Reports was a long scroll away). */}
                    <nav aria-label="Jump to a card" className={cn(TYPE.tc, "flex flex-wrap items-center gap-x-3 gap-y-1 pl-1 text-gray-500")}>
                        <span>Jump to</span>
                        {[
                            { id: "documents", label: "Documents" },
                            { id: "requests", label: "Requests", n: sentenceReady ? requestGroups.needsUs.length : 0 },
                            ...(showQuestions ? [{ id: "asks", label: "Questions", n: sentenceReady ? groups.waiting.length : 0 }] : []),
                            { id: "note", label: "Note" },
                            { id: "reports", label: "Reports" },
                        ].map((j) => (
                            <button
                                key={j.id}
                                type="button"
                                onClick={() => openCard(j.id)}
                                className={cn(FOCUS_RING, "rounded font-medium text-blue-700 hover:underline")}
                            >
                                {j.label}
                                {j.n ? <span className="ml-1 tabular-nums text-gray-500">{j.n}</span> : null}
                            </button>
                        ))}
                    </nav>
                    <span className="ml-auto">
                        {/* A notice that names where it opens (a doc the
                            client answered in, a change they asked for)
                            lands there: on this tab, through ?doc= or
                            ?request=. */}
                        <NotificationBell
                            audience="staff"
                            businessId={businessId}
                            onOpen={(n) => {
                                const to = noticeLink(n);
                                if (to) navigate(to);
                            }}
                        />
                    </span>
                </div>

                {/* The docs Oura reads from the client's folder: full width,
                    first, because they are where the client answers now. */}
                {/* key: a client switch remounts the board, so no creation draft or prefill outlives its client. openDocId and createRequest live here and survive the remount. */}
                <DocumentsBoard key={businessId} load={docs} businessId={businessId} businessName={business.name} openDocId={openDocId} onOpenDoc={openDoc} createRequest={createRequest} onCreateTaken={() => setCreateRequest(null)} />

                {/* Each column at its own height: the lists run long or short
                    with the client, and a card stretched to the other column
                    would end in empty white. The lists take the wider column,
                    what the client started first: beside the note at 1024 a
                    question's title had 140px and was cut at 2 lines. The
                    note leads the other column. With no questions sent, the
                    folder card moves under the requests so the two columns
                    still end close to level: beside the note it would leave
                    the requests column nearly 300px short. At 1280px on the
                    sample data (questions sent) they end about 110px apart.
                    Which column that is waits for the questions' read, so
                    the folder card shows once they are read (or failed)
                    and never jumps column, remounting, as they land.
                    Stacked below 1280px. */}
                <Bento layout="c21xl">
                    <BentoStack>
                        <RequestsCard
                            groups={requestGroups}
                            requests={requests}
                            callsLoading={packs === null}
                            callsFailed={loadError !== null}
                            marks={marks}
                            itemTitle={itemTitle}
                            businessName={business.name}
                            onOpen={openAsk}
                        />
                        {showQuestions && <QuestionsCard groups={groups} loading={packs === null} failed={loadError !== null} onOpen={openAsk} />}
                        {questionsRead && !showQuestions && <FolderCard businessId={businessId} load={docs} />}
                        {/* The reports this client has been sent. On the
                            requests column, under the lists: an upload is
                            started from this tab's More menu, so the result
                            belongs on the same tab (#4009). */}
                        <ReportsCard businessId={businessId} reloadKey={reportsSeq} />
                        {/* What the client's Google Business Profile says now,
                            per outlet, which the GBP Pack is written from
                            (#4010). This tab, because the GBP Pack is a client
                            doc. Staff only, as the routes are -- a merchant
                            never reaches this screen, and the server refuses
                            them as well. No flag: the routes are served in
                            every environment. */}
                        <GbpProfileCard businessId={businessId} />
                    </BentoStack>
                    <BentoStack>
                        <NoteComposer businessId={businessId} businessName={business.name} focusRequest={noteFocus} />
                        {questionsRead && showQuestions && <FolderCard businessId={businessId} load={docs} />}
                    </BentoStack>
                </Bento>
            </div>

            {/* One drawer for questions, requests, calls and packs: rows open it
                with their group, and previous and next walk that group. */}
            <AskDrawer
                target={ask?.target ?? null}
                seq={ask?.seq ?? null}
                onClose={() => setAsk(null)}
                onOpen={openAsk}
                packs={packs ?? []}
                threads={threads}
                calls={calls}
                changes={requests.data ?? []}
                groups={groups}
                requestGroups={requestGroups}
                taskById={taskById}
                businessId={businessId}
                onChanged={refresh}
                hasG5={hasG5}
                itemTitle={itemTitle}
            />
            <StartPlanWizard
                open={startPlanOpen}
                businessId={businessId}
                businessName={business.name}
                category={business.vertical}
                scope={scopeVerdictOf(scope)}
                onOpenChange={setStartPlanOpen}
                onActivated={refresh}
            />
            <CreateLoginDialog open={loginOpen} businessId={businessId} onOpenChange={setLoginOpen} />
            <SwitchWorkspaceDialog
                isOpen={switchOpen}
                onClose={() => setSwitchOpen(false)}
                onConfirm={enterMerchantWorkspace}
                companyName={business.name}
            />
            <UploadReportDialog
                open={reportOpen}
                businessId={businessId}
                onOpenChange={setReportOpen}
                onUploaded={() => setReportsSeq((n) => n + 1)}
            />
        </FoldScope>
    );
}
